import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Download, Wallet, BadgeCheck, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function Stat({ label, value, tone, testId }) {
  const bg = { indigo: "bg-[#EEF2FF]", green: "bg-[#F0FDF4]", amber: "bg-[#FFFBEB]", slate: "bg-white" }[tone] || "bg-white";
  return (
    <div className={`card-shell p-5 ${bg}`} data-testid={testId}>
      <div className="data-label">{label}</div>
      <div className="mt-2 font-heading font-black text-3xl tracking-tight">{value}</div>
    </div>
  );
}

export default function PayoutsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [ledger, setLedger] = useState(null);
  const [note, setNote] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/payouts?month=${month}`);
    setLedger(r.data);
    // seed note map with existing values
    const nm = {};
    (r.data.rows || []).forEach((row) => { nm[row.agent_id] = row.note || ""; });
    setNote(nm);
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const togglePaid = async (row) => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      await api.post("/payouts/mark_paid", {
        agent_id: row.agent_id,
        month,
        paid: !row.paid,
        note: note[row.agent_id] || "",
      });
      toast.success(row.paid ? "Marked unpaid" : "Marked paid");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const saveNoteOnly = async (row) => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      await api.post("/payouts/mark_paid", {
        agent_id: row.agent_id,
        month,
        paid: row.paid,
        note: note[row.agent_id] || "",
      });
      toast.success("Note saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const csvUrl = `${process.env.REACT_APP_BACKEND_URL}/api/payouts/export.csv?month=${month}`;

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="data-label mb-1">Finance ledger</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">Payouts</h1>
          <p className="text-sm text-[#71717A] mt-1 max-w-2xl">
            Monthly batch of approved incentives per Sales Agent. Only records approved by the Sales Incentives team appear here.
            Admin can mark payouts as paid once finance disburses them.
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="data-label mb-1 block">Payout month</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-[180px]"
              data-testid="payout-month-input"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => window.open(csvUrl, "_blank")} data-testid="export-payouts-csv">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {ledger && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Agents on ledger" value={ledger.totals.agents} tone="slate" testId="payout-stat-agents" />
          <Stat label="Total incentive" value={`₹${Number(ledger.totals.grand_total || 0).toLocaleString("en-IN")}`} tone="indigo" testId="payout-stat-total" />
          <Stat label="Paid" value={`₹${Number(ledger.totals.paid_total || 0).toLocaleString("en-IN")}`} tone="green" testId="payout-stat-paid" />
          <Stat label="Outstanding" value={`₹${Number(ledger.totals.unpaid_total || 0).toLocaleString("en-IN")}`} tone="amber" testId="payout-stat-unpaid" />
        </div>
      )}

      <div className="card-shell overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Salesperson ID</th>
                <th>Store</th>
                <th className="text-right">Registrations</th>
                <th className="text-right">Incentive (₹)</th>
                <th>Paid</th>
                <th>Finance note</th>
                <th>When / By</th>
              </tr>
            </thead>
            <tbody data-testid="payouts-tbody">
              {!ledger || ledger.rows.length === 0 ? (
                <tr><td colSpan={8} className="p-10 text-center text-[#71717A] text-sm">
                  <Wallet className="w-8 h-8 mx-auto mb-2 text-[#A1A1AA]" />
                  No approved incentives for {month}.
                </td></tr>
              ) : ledger.rows.map((r) => (
                <tr key={r.agent_id} data-testid={`payout-row-${r.agent_id}`}>
                  <td>
                    <div className="font-medium">{r.agent_name || "—"}</div>
                    <div className="text-xs text-[#71717A]">{r.agent_email}</div>
                  </td>
                  <td className="font-mono text-xs">{r.salesperson_id || "—"}</td>
                  <td className="text-sm">{r.store_name || "—"}</td>
                  <td className="text-right font-mono">{r.count}</td>
                  <td className="text-right font-mono font-medium text-[#059669]">₹{Number(r.total).toLocaleString("en-IN")}</td>
                  <td>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <Checkbox
                        checked={r.paid}
                        onCheckedChange={() => togglePaid(r)}
                        disabled={!isAdmin || busy}
                        data-testid={`payout-check-${r.agent_id}`}
                      />
                      {r.paid ? (
                        <span className="inline-flex items-center gap-1 text-[#166534]">
                          <BadgeCheck className="w-3 h-3" /> Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#92400E]">
                          <AlertCircle className="w-3 h-3" /> Pending
                        </span>
                      )}
                    </label>
                  </td>
                  <td className="min-w-[220px]">
                    <div className="flex gap-1">
                      <Input
                        value={note[r.agent_id] || ""}
                        onChange={(e) => setNote({ ...note, [r.agent_id]: e.target.value })}
                        placeholder={isAdmin ? "NEFT ref, cheque #, batch date" : ""}
                        readOnly={!isAdmin}
                        className="text-xs"
                        data-testid={`payout-note-${r.agent_id}`}
                      />
                      {isAdmin && (note[r.agent_id] || "") !== (r.note || "") && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => saveNoteOnly(r)}
                          disabled={busy}
                          data-testid={`payout-note-save-${r.agent_id}`}
                        >
                          Save
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="text-xs text-[#71717A]">
                    {r.paid_at ? (
                      <>
                        <div>{(r.paid_at || "").slice(0, 10)}</div>
                        <div className="text-[10px]">by {r.paid_by || "—"}</div>
                      </>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
